import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { 
  X,
  Plus,
  Minus,
  Heart,
  Star,
  CaretLeft,
  Coffee,
  Leaf,
  ShoppingBag,
  Trash,
  MapPin,
  Phone,
  CreditCard,
  Check,
  CaretRight,
  Receipt,
  Clock,
  Storefront,
  ForkKnife,
  Cookie,
  Drop,
  Plant,
  Sparkle,
  Tag,
  ArrowRight,
  User
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BRAND } from "@/styles/brand";

const API = process.env.REACT_APP_BACKEND_URL;
const C = BRAND.colors;

// ═══════════════════════════════════════════════════════════════════════════
// MODERN ONLINE ORDER PAGE - Premium Design
// Colors unified via BRAND system (brand.js)
// ═══════════════════════════════════════════════════════════════════════════

const COLORS = {
  bg: C.bg,
  bgDark: C.bgSecondary,
  green: C.green,
  greenLight: C.greenLight,
  orange: C.orange,
  orangeLight: C.orangeLight,
  tan: C.tan,
  red: C.error,
  text: C.text,
  textMuted: C.textMuted,
  white: "#FFFFFF",
  card: C.bgCard,
  border: C.border,
};

// Category Icons (Phosphor Icons instead of emojis)
const CategoryIcon = ({ id, size = 18, weight = "fill" }) => {
  const icons = {
    all: <Sparkle size={size} weight={weight} />,
    coffee: <Coffee size={size} weight={weight} />,
    matcha: <Drop size={size} weight={weight} />,
    food: <ForkKnife size={size} weight={weight} />,
    sweets: <Cookie size={size} weight={weight} />,
    cbd: <Plant size={size} weight={weight} />,
  };
  return icons[id] || <Sparkle size={size} weight={weight} />;
};

// Menu Categories
const CATEGORIES = [
  { id: "all", name: "Alle" },
  { id: "coffee", name: "Kaffee" },
  { id: "matcha", name: "Matcha" },
  { id: "food", name: "Essen" },
  { id: "sweets", name: "Süßes" },
  { id: "pasta", name: "Pasta" },
];

// Product Options
const SIZE_OPTIONS = [
  { id: "small", name: "S", fullName: "Klein", priceAdd: 0 },
  { id: "medium", name: "M", fullName: "Mittel", priceAdd: 0.80 },
  { id: "large", name: "L", fullName: "Groß", priceAdd: 1.50 },
];

const MILK_OPTIONS = [
  { id: "regular", name: "Normal", priceAdd: 0 },
  { id: "oat", name: "Hafer", priceAdd: 0.50 },
  { id: "soy", name: "Soja", priceAdd: 0.40 },
  { id: "almond", name: "Mandel", priceAdd: 0.60 },
  { id: "coconut", name: "Kokos", priceAdd: 0.70 },
];

const EXTRAS = [
  { id: "extra_shot", name: "Extraschuss", priceAdd: 0.70 },
  { id: "vanilla", name: "Vanille", priceAdd: 0.50 },
  { id: "caramel", name: "Karamell", priceAdd: 0.50 },
  { id: "whipped", name: "Sahne", priceAdd: 0.60 },
];

// Menu Items
const MENU_ITEMS = [
  {
    id: 1,
    category: "coffee",
    name: "Cappuccino",
    description: "Cremiger Milchschaum, perfekter Espresso",
    price: 3.90,
    image: "https://images.unsplash.com/photo-1572442388796-11668a67e53d?w=800",
    popular: true,
    rating: 4.9,
    hasSize: true,
    hasMilk: true,
    hasExtras: true,
  },
  {
    id: 2,
    category: "matcha",
    name: "Matcha Latte",
    description: "Premium Kyoto Matcha, schaumig aufgeschlagen",
    price: 4.50,
    image: "https://images.unsplash.com/photo-1536256263959-770b48d82b0a?w=800",
    popular: true,
    rating: 4.8,
    hasSize: true,
    hasMilk: true,
    hasExtras: false,
  },
  {
    id: 3,
    category: "sweets",
    name: "NY Cheesecake",
    description: "Original Rezept, cremig & reichhaltig",
    price: 5.90,
    image: "https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=800",
    popular: true,
    rating: 5.0,
    hasSize: false,
    hasMilk: false,
    hasExtras: false,
  },
  {
    id: 4,
    category: "food",
    name: "Avocado Toast",
    description: "Sauerteig, Avocado, pochiertes Ei",
    price: 9.90,
    image: "https://images.unsplash.com/photo-1541519227354-08fa5d50c44d?w=800",
    rating: 4.7,
    hasSize: false,
    hasMilk: false,
    hasExtras: false,
  },
  {
    id: 5,
    category: "coffee",
    name: "Flat White",
    description: "Doppelter Espresso, samtiger Microfoam",
    price: 4.20,
    image: "https://images.unsplash.com/photo-1577968897966-3d4325b36b61?w=800",
    rating: 4.6,
    hasSize: true,
    hasMilk: true,
    hasExtras: true,
  },
  {
    id: 6,
    category: "matcha",
    name: "Iced Matcha",
    description: "Erfrischend kalt mit Kokosmilch",
    price: 5.20,
    image: "https://images.unsplash.com/photo-1515823064-d6e0c04616a7?w=800",
    rating: 4.7,
    hasSize: true,
    hasMilk: true,
    hasExtras: false,
  },
  {
    id: 7,
    category: "cbd",
    name: "Cheesecake Slice",
    description: "Hausgemacht mit frischen Zutaten",
    price: 7.90,
    image: "https://images.unsplash.com/photo-1606313564200-e75d5e30476c?w=800",
    rating: 4.5,
    hasSize: false,
    hasMilk: false,
    hasExtras: false,
  },
  {
    id: 8,
    category: "food",
    name: "Buddha Bowl",
    description: "Quinoa, geröstetes Gemüse, Tahini",
    price: 11.90,
    image: "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800",
    rating: 4.8,
    hasSize: false,
    hasMilk: false,
    hasExtras: false,
  },
  {
    id: 9,
    category: "sweets",
    name: "Tiramisu",
    description: "Klassisch italienisch, Mascarpone & Espresso",
    price: 6.50,
    image: "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=800",
    rating: 4.8,
    hasSize: false,
    hasMilk: false,
    hasExtras: false,
  },
  {
    id: 10,
    category: "coffee",
    name: "Espresso",
    description: "Kurz, stark, unvergesslich",
    price: 2.50,
    image: "https://images.unsplash.com/photo-1510707577719-ae7c14805e3a?w=800",
    rating: 4.9,
    hasSize: false,
    hasMilk: false,
    hasExtras: true,
  },
];

const DELIVERY_FEE = 2.50;
const MIN_ORDER = 10.00;

// ═══════════════════════════════════════════════════════════════════════════
// PRODUCT DETAIL MODAL - Modern Slide-up
// ═══════════════════════════════════════════════════════════════════════════
const ProductDetailModal = ({ product, isOpen, onClose, onAddToCart }) => {
  const [selectedSize, setSelectedSize] = useState("medium");
  const [selectedMilk, setSelectedMilk] = useState("regular");
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setSelectedSize("medium");
      setSelectedMilk("regular");
      setSelectedExtras([]);
      setQuantity(1);
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const sizePrice = product.hasSize ? SIZE_OPTIONS.find(s => s.id === selectedSize)?.priceAdd || 0 : 0;
  const milkPrice = product.hasMilk ? MILK_OPTIONS.find(m => m.id === selectedMilk)?.priceAdd || 0 : 0;
  const extrasPrice = selectedExtras.reduce((sum, extraId) => {
    const extra = EXTRAS.find(e => e.id === extraId);
    return sum + (extra?.priceAdd || 0);
  }, 0);
  const unitPrice = product.price + sizePrice + milkPrice + extrasPrice;
  const itemTotal = unitPrice * quantity;

  const toggleExtra = (extraId) => {
    setSelectedExtras(prev => 
      prev.includes(extraId) 
        ? prev.filter(id => id !== extraId)
        : [...prev, extraId]
    );
  };

  const handleAddToCart = () => {
    const cartItem = {
      id: `${product.id}-${Date.now()}`,
      product_id: product.id,
      product_name: product.name,
      quantity,
      size: product.hasSize ? selectedSize : null,
      milk: product.hasMilk ? selectedMilk : null,
      extras: selectedExtras,
      unit_price: unitPrice,
      total_price: itemTotal,
      image: product.image
    };
    onAddToCart(cartItem);
    toast.success(`${quantity}x ${product.name} hinzugefügt`, {
      duration: 2000,
      style: { background: COLORS.green, color: COLORS.white }
    });
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: "rgba(26,26,26,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 32, stiffness: 400 }}
        className="w-full max-w-lg rounded-t-3xl overflow-hidden"
        style={{ backgroundColor: COLORS.white, maxHeight: "85vh", marginBottom: "60px" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Product Image with Gradient Overlay */}
        <div className="relative h-52 overflow-hidden">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          
          {/* Close Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-md"
            style={{ backgroundColor: "rgba(255,255,255,0.9)" }}
          >
            <X size={20} weight="bold" style={{ color: COLORS.text }} />
          </motion.button>

          {/* Popular Badge */}
          {product.popular && (
            <div className="absolute bottom-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full backdrop-blur-md"
              style={{ backgroundColor: "rgba(45,90,61,0.9)" }}>
              <Sparkle size={14} weight="fill" className="text-white" />
              <span className="text-xs font-bold text-white tracking-wide">BELIEBT</span>
            </div>
          )}

          {/* Rating Badge */}
          <div className="absolute bottom-4 right-4 flex items-center gap-1 px-2.5 py-1.5 rounded-full backdrop-blur-md"
            style={{ backgroundColor: "rgba(255,255,255,0.95)" }}>
            <Star size={14} weight="fill" style={{ color: "#F59E0B" }} />
            <span className="text-sm font-bold" style={{ color: COLORS.text }}>{product.rating}</span>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto px-5 py-4 space-y-4" style={{ maxHeight: "calc(85vh - 13rem - 80px)" }}>
          {/* Title & Price */}
          <div>
            <h2 className="text-xl font-black tracking-tight" style={{ color: COLORS.text }}>
              {product.name}
            </h2>
            <p className="mt-1 text-sm" style={{ color: COLORS.textMuted }}>{product.description}</p>
            <p className="mt-2 text-xl font-black" style={{ color: COLORS.green }}>
              ab {product.price.toFixed(2)}€
            </p>
          </div>

          {/* Size Options - Pill Style */}
          {product.hasSize && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textMuted }}>
                Größe
              </p>
              <div className="flex gap-2">
                {SIZE_OPTIONS.map(size => (
                  <motion.button
                    key={size.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedSize(size.id)}
                    className="flex-1 py-2.5 rounded-xl font-bold text-sm transition-all"
                    style={{
                      backgroundColor: selectedSize === size.id ? COLORS.green : COLORS.bgDark,
                      color: selectedSize === size.id ? COLORS.white : COLORS.text,
                      boxShadow: selectedSize === size.id ? `0 4px 14px ${COLORS.green}40` : "none"
                    }}
                  >
                    <span className="block text-base">{size.name}</span>
                    <span className="block text-xs opacity-70">
                      {size.priceAdd > 0 ? `+${size.priceAdd.toFixed(2)}€` : "inkl."}
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Milk Options - Horizontal Scroll */}
          {product.hasMilk && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textMuted }}>
                Milch
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {MILK_OPTIONS.map(milk => (
                  <motion.button
                    key={milk.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedMilk(milk.id)}
                    className="px-3 py-2 rounded-xl font-semibold text-sm whitespace-nowrap transition-all"
                    style={{
                      backgroundColor: selectedMilk === milk.id ? COLORS.green : COLORS.bgDark,
                      color: selectedMilk === milk.id ? COLORS.white : COLORS.text,
                    }}
                  >
                    {milk.name}
                    {milk.priceAdd > 0 && (
                      <span className="ml-1 opacity-70">+{milk.priceAdd.toFixed(2)}€</span>
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Extras - Chip Style */}
          {product.hasExtras && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textMuted }}>
                Extras
              </p>
              <div className="flex flex-wrap gap-2">
                {EXTRAS.map(extra => (
                  <motion.button
                    key={extra.id}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => toggleExtra(extra.id)}
                    className="px-3 py-2 rounded-xl font-semibold text-sm transition-all"
                    style={{
                      backgroundColor: selectedExtras.includes(extra.id) ? COLORS.orange : COLORS.bgDark,
                      color: selectedExtras.includes(extra.id) ? COLORS.white : COLORS.text,
                    }}
                  >
                    {extra.name}
                    <span className="ml-1 opacity-70">+{extra.priceAdd.toFixed(2)}€</span>
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Fixed Bottom - Quantity & Add */}
        <div className="p-4 border-t" style={{ backgroundColor: COLORS.white, borderColor: COLORS.border }}>
          <div className="flex items-center gap-4">
            {/* Quantity Selector */}
            <div className="flex items-center gap-1 p-1 rounded-2xl" style={{ backgroundColor: COLORS.bgDark }}>
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: COLORS.white }}
              >
                <Minus size={16} weight="bold" style={{ color: COLORS.text }} />
              </motion.button>
              <span className="w-8 text-center font-black text-lg" style={{ color: COLORS.text }}>{quantity}</span>
              <motion.button
                whileTap={{ scale: 0.85 }}
                onClick={() => setQuantity(quantity + 1)}
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: COLORS.white }}
              >
                <Plus size={16} weight="bold" style={{ color: COLORS.text }} />
              </motion.button>
            </div>

            {/* Add to Cart Button */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={handleAddToCart}
              className="flex-1 py-4 rounded-2xl font-black text-white flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: COLORS.green,
                boxShadow: `0 6px 20px ${COLORS.green}40`
              }}
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={20} weight="fill" />
              <span>Hinzufügen · {itemTotal.toFixed(2)}€</span>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CART SIDEBAR - Modern Slide-in
// ═══════════════════════════════════════════════════════════════════════════
const CartSidebar = ({ isOpen, onClose, cart, onUpdateQuantity, onRemove, onCheckout, subtotal }) => {
  if (!isOpen) return null;

  const canCheckout = subtotal >= MIN_ORDER;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50"
      style={{ backgroundColor: "rgba(26,26,26,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
        className="absolute right-0 top-0 h-full w-full max-w-md flex flex-col"
        style={{ backgroundColor: COLORS.bg }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between" style={{ backgroundColor: COLORS.white }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: COLORS.green }}>
              <ShoppingBag size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h2 className="font-black text-lg" style={{ color: COLORS.text }}>Warenkorb</h2>
              <p className="text-xs" style={{ color: COLORS.textMuted }}>{cart.length} Artikel</p>
            </div>
          </div>
          <motion.button 
            whileTap={{ scale: 0.9 }} 
            onClick={onClose}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.bgDark }}
          >
            <X size={20} weight="bold" style={{ color: COLORS.text }} />
          </motion.button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {cart.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4" 
                style={{ backgroundColor: COLORS.bgDark }}>
                <ShoppingBag size={36} weight="light" style={{ color: COLORS.textMuted }} />
              </div>
              <p className="font-bold text-lg" style={{ color: COLORS.text }}>Dein Warenkorb ist leer</p>
              <p className="text-sm mt-1" style={{ color: COLORS.textMuted }}>Füge leckere Produkte hinzu!</p>
            </div>
          ) : (
            cart.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: 100 }}
                className="p-3 rounded-2xl flex gap-3"
                style={{ backgroundColor: COLORS.white }}
              >
                <img src={item.image} alt={item.product_name} className="w-20 h-20 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold truncate" style={{ color: COLORS.text }}>{item.product_name}</p>
                      {(item.size || item.milk || item.extras?.length > 0) && (
                        <p className="text-xs mt-0.5 truncate" style={{ color: COLORS.textMuted }}>
                          {[
                            item.size && SIZE_OPTIONS.find(s => s.id === item.size)?.fullName,
                            item.milk && item.milk !== "regular" && MILK_OPTIONS.find(m => m.id === item.milk)?.name,
                            ...(item.extras?.map(e => EXTRAS.find(ex => ex.id === e)?.name) || [])
                          ].filter(Boolean).join(" · ")}
                        </p>
                      )}
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.9 }}
                      onClick={() => onRemove(item.id)}
                      className="p-1.5 rounded-lg"
                      style={{ backgroundColor: COLORS.bgDark }}
                    >
                      <Trash size={16} style={{ color: COLORS.red }} />
                    </motion.button>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ backgroundColor: COLORS.bgDark }}>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                        className="w-7 h-7 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: COLORS.white }}
                      >
                        <Minus size={12} weight="bold" />
                      </motion.button>
                      <span className="w-6 text-center font-bold text-sm">{item.quantity}</span>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-md flex items-center justify-center"
                        style={{ backgroundColor: COLORS.white }}
                      >
                        <Plus size={12} weight="bold" />
                      </motion.button>
                    </div>
                    <span className="font-black" style={{ color: COLORS.green }}>
                      {item.total_price.toFixed(2)}€
                    </span>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="p-4 border-t" style={{ backgroundColor: COLORS.white, borderColor: COLORS.border }}>
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span style={{ color: COLORS.textMuted }}>Zwischensumme</span>
                <span style={{ color: COLORS.text }}>{subtotal.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-sm">
                <span style={{ color: COLORS.textMuted }}>Liefergebühr</span>
                <span style={{ color: COLORS.text }}>ab 2,50€</span>
              </div>
              <div className="flex justify-between font-black text-lg pt-3 border-t" style={{ borderColor: COLORS.border }}>
                <span>Gesamt</span>
                <span style={{ color: COLORS.green }}>{(subtotal + 2.50).toFixed(2)}€</span>
              </div>
            </div>

            {!canCheckout && (
              <div className="p-3 rounded-xl mb-3 text-center" style={{ backgroundColor: COLORS.orange + "15" }}>
                <p className="text-sm font-medium" style={{ color: COLORS.orange }}>
                  Mindestbestellwert: {MIN_ORDER.toFixed(2)}€
                </p>
                <p className="text-xs mt-0.5" style={{ color: COLORS.textMuted }}>
                  Noch {(MIN_ORDER - subtotal).toFixed(2)}€ hinzufügen
                </p>
              </div>
            )}

            <motion.button
              whileTap={{ scale: canCheckout ? 0.97 : 1 }}
              onClick={canCheckout ? onCheckout : undefined}
              className="w-full py-4 rounded-2xl font-black text-white flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: canCheckout ? COLORS.green : COLORS.textMuted,
                boxShadow: canCheckout ? `0 6px 20px ${COLORS.green}40` : "none"
              }}
              data-testid="checkout-btn"
            >
              Zur Kasse
              <ArrowRight size={20} weight="bold" />
            </motion.button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// CHECKOUT MODAL - Complete Flow with PLZ Check First
// ═══════════════════════════════════════════════════════════════════════════

const CheckoutModal = ({ isOpen, onClose, cart, subtotal, user, token, onSuccess, userMembership }) => {
  const navigate = useNavigate();
  // Steps: 0: PLZ Check, 1: Address, 2: Payment, 3: Waiting, 4: Confirmed
  const [step, setStep] = useState(0);
  const [orderType, setOrderType] = useState("delivery");
  const [address, setAddress] = useState(user?.address || "");
  const [plz, setPlz] = useState(user?.plz || "");
  const [city, setCity] = useState(user?.city || "Aachen");
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [paymentMethod, setPaymentMethod] = useState("cash"); // cash, card, online
  const [loading, setLoading] = useState(false);
  const [checkingPLZ, setCheckingPLZ] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [deliveryError, setDeliveryError] = useState("");
  const [pollingInterval, setPollingInterval] = useState(null);
  
  // Delivery Zone Info
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState(2.50);
  const [minOrder, setMinOrder] = useState(10.0);

  // Calculate potential savings with membership
  const coffeeCount = cart.filter(item => 
    item.product_name.toLowerCase().includes("cappuccino") ||
    item.product_name.toLowerCase().includes("latte") ||
    item.product_name.toLowerCase().includes("espresso") ||
    item.product_name.toLowerCase().includes("flat white")
  ).reduce((sum, item) => sum + item.quantity, 0);
  
  const matchaCount = cart.filter(item => 
    item.product_name.toLowerCase().includes("matcha")
  ).reduce((sum, item) => sum + item.quantity, 0);

  const potentialMonthlySavings = (coffeeCount * 3.90 * 4) + (matchaCount * 4.50 * 4);
  const showMembershipUpsell = !userMembership && (coffeeCount > 0 || matchaCount > 0);

  useEffect(() => {
    if (isOpen) {
      setStep(0); // Start with PLZ check
      setOrderType("delivery");
      setAddress(user?.address || "");
      setPlz(user?.plz || "");
      setCity(user?.city || "Aachen");
      setNotes("");
      setPhone(user?.phone || "");
      setPaymentMethod("cash");
      setOrderResult(null);
      setDeliveryError("");
      setDeliveryInfo(null);
    }
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [isOpen, user]);

  // Check PLZ with Backend
  const checkDeliveryZone = async () => {
    if (!plz || plz.length < 5) {
      setDeliveryError("Bitte gültige 5-stellige PLZ eingeben");
      return;
    }
    
    setCheckingPLZ(true);
    setDeliveryError("");
    
    try {
      const res = await axios.post(`${API}/api/delivery/check`, { plz });
      
      if (res.data.available) {
        setDeliveryInfo(res.data);
        setDeliveryFee(res.data.delivery_fee);
        setMinOrder(res.data.min_order);
        
        // Check minimum order
        if (subtotal < res.data.min_order) {
          setDeliveryError(`Mindestbestellwert für ${res.data.name || plz}: ${res.data.min_order.toFixed(2)}€ (du hast ${subtotal.toFixed(2)}€)`);
        } else {
          // Success! Move to address step
          toast.success(res.data.message);
          setStep(1);
        }
      } else {
        setDeliveryError(res.data.message);
      }
    } catch (err) {
      setDeliveryError("Fehler bei der PLZ-Prüfung. Bitte versuche es erneut.");
    }
    setCheckingPLZ(false);
  };

  // Switch to pickup
  const handlePickup = () => {
    setOrderType("pickup");
    setDeliveryFee(0);
    setMinOrder(5.0);
    setStep(1);
  };

  // Poll for order status
  const startPollingOrderStatus = (orderId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/orders/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const order = res.data;
        if (order.status === "confirmed" || order.status === "preparing") {
          clearInterval(interval);
          setOrderResult(order);
          setStep(4); // Move to confirmed
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 5000);
    setPollingInterval(interval);
  };

  const handleContinueToPayment = () => {
    if (orderType === "delivery") {
      if (!address.trim()) {
        toast.error("Bitte Adresse eingeben");
        return;
      }
      if (!phone.trim()) {
        toast.error("Bitte Telefonnummer eingeben");
        return;
      }
    }
    setStep(2);
  };

  const handleSubmitOrder = async () => {
    setLoading(true);
    try {
      const fullAddress = orderType === "delivery" 
        ? `${address}, ${plz} ${city}`
        : "Abholung in der Filiale";

      const currentDeliveryFee = orderType === "delivery" ? deliveryFee : 0;
      const total = subtotal + currentDeliveryFee;

      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          size: item.size,
          milk: item.milk,
          extras: item.extras || [],
          unit_price: item.unit_price,
          total_price: item.total_price
        })),
        delivery_address: fullAddress,
        delivery_notes: notes,
        phone: phone,
        subtotal: subtotal,
        delivery_fee: currentDeliveryFee,
        total: total,
        order_type: orderType,
        payment_method: paymentMethod
      };

      const res = await axios.post(`${API}/api/orders/create`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOrderResult(res.data.order);
      setStep(3); // Waiting for acceptance
      startPollingOrderStatus(res.data.order.id);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Bestellen");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const currentDeliveryFee = orderType === "delivery" ? deliveryFee : 0;
  const total = subtotal + currentDeliveryFee;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26,26,26,0.7)", backdropFilter: "blur(4px)" }}
      onClick={step < 3 ? onClose : undefined}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md rounded-3xl overflow-hidden"
        style={{ backgroundColor: COLORS.bg, maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* ═══ STEP 0: PLZ CHECK ═══ */}
        {step === 0 && (
          <>
            {/* Header */}
            <div className="p-5 flex items-center justify-between" style={{ backgroundColor: COLORS.green }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20">
                  <MapPin size={20} weight="fill" className="text-white" />
                </div>
                <div className="text-white">
                  <h2 className="font-black text-lg">Wohin liefern?</h2>
                  <p className="text-xs opacity-80">Prüfe zuerst deine PLZ</p>
                </div>
              </div>
              <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
                className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/20">
                <X size={20} weight="bold" className="text-white" />
              </motion.button>
            </div>

            <div className="p-6 space-y-5">
              {/* PLZ Input */}
              <div className="text-center mb-6">
                <p className="text-sm mb-4" style={{ color: COLORS.textMuted }}>
                  Gib deine Postleitzahl ein, um zu prüfen ob wir zu dir liefern können
                </p>
                
                <div className="relative">
                  <input
                    type="text"
                    value={plz}
                    onChange={(e) => { 
                      const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                      setPlz(val); 
                      setDeliveryError(""); 
                    }}
                    placeholder="z.B. 52062"
                    maxLength={5}
                    className="w-full p-5 rounded-2xl text-center text-2xl font-black tracking-widest"
                    style={{ 
                      backgroundColor: COLORS.white, 
                      color: COLORS.text, 
                      border: `3px solid ${deliveryError ? COLORS.red : plz.length === 5 ? COLORS.green : COLORS.border}` 
                    }}
                    data-testid="plz-check-input"
                    autoFocus
                  />
                  {plz.length === 5 && !deliveryError && !deliveryInfo && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute right-4 top-1/2 -translate-y-1/2"
                    >
                      <Check size={24} weight="bold" style={{ color: COLORS.green }} />
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {deliveryError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: COLORS.red + "15", border: `2px solid ${COLORS.red}` }}
                >
                  <p className="text-sm font-medium text-center" style={{ color: COLORS.red }}>
                    {deliveryError}
                  </p>
                </motion.div>
              )}

              {/* Check Button */}
              <motion.button
                whileTap={{ scale: checkingPLZ ? 1 : 0.97 }}
                onClick={checkDeliveryZone}
                disabled={plz.length < 5 || checkingPLZ}
                className="w-full py-4 rounded-2xl font-black text-white flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: plz.length === 5 ? COLORS.green : COLORS.textMuted,
                  boxShadow: plz.length === 5 ? `0 6px 20px ${COLORS.green}40` : "none"
                }}
                data-testid="check-plz-btn"
              >
                {checkingPLZ ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <MapPin size={22} />
                  </motion.div>
                ) : (
                  <>
                    <MapPin size={20} weight="bold" />
                    PLZ prüfen
                  </>
                )}
              </motion.button>

              {/* Divider */}
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
                <span className="text-xs font-bold" style={{ color: COLORS.textMuted }}>ODER</span>
                <div className="flex-1 h-px" style={{ backgroundColor: COLORS.border }} />
              </div>

              {/* Pickup Option */}
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handlePickup}
                className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all"
                style={{ backgroundColor: COLORS.white, border: `2px solid ${COLORS.border}` }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: COLORS.orange + "15" }}>
                  <Storefront size={24} weight="fill" style={{ color: COLORS.orange }} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold" style={{ color: COLORS.text }}>Selbst abholen</p>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>
                    Kostenlos · Pontstraße 137, Aachen
                  </p>
                </div>
                <CaretRight size={20} style={{ color: COLORS.textMuted }} />
              </motion.button>

              {/* Info about delivery areas */}
              <div className="p-3 rounded-xl text-center" style={{ backgroundColor: COLORS.bgDark }}>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>
                  🚴 Liefergebiete: Aachen & Umgebung (52062-52080)
                </p>
              </div>
            </div>
          </>
        )}
        {/* ═══ STEP 3: Waiting for Acceptance ═══ */}
        {step === 3 && orderResult && (
          <div className="p-8 text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-24 h-24 rounded-full mx-auto flex items-center justify-center mb-6"
              style={{ backgroundColor: COLORS.orange + "20" }}
            >
              <Clock size={48} weight="fill" style={{ color: COLORS.orange }} />
            </motion.div>
            
            <h2 className="text-2xl font-black mb-2" style={{ color: COLORS.text }}>
              Warten auf Bestätigung
            </h2>
            <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
              Das Restaurant prüft deine Bestellung...
            </p>

            <div className="p-5 rounded-2xl text-left mb-6" style={{ backgroundColor: COLORS.white }}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm" style={{ color: COLORS.textMuted }}>Bestellnummer</span>
                <span className="font-black text-xl" style={{ color: COLORS.orange }}>
                  #{orderResult.order_number}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: COLORS.textMuted }}>Status</span>
                <span className="px-3 py-1 rounded-full text-sm font-bold" 
                  style={{ backgroundColor: COLORS.orange + "20", color: COLORS.orange }}>
                  Ausstehend
                </span>
              </div>
            </div>

            <motion.div
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex items-center justify-center gap-2 text-sm"
              style={{ color: COLORS.textMuted }}
            >
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.orange }} />
              Wird geprüft...
            </motion.div>
          </div>
        )}

        {/* ═══ STEP 4: Order Confirmed ═══ */}
        {step === 4 && orderResult && (
          <div className="p-8 text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 15 }}
              className="w-24 h-24 rounded-full mx-auto flex items-center justify-center mb-6"
              style={{ backgroundColor: COLORS.green }}
            >
              <Check size={48} weight="bold" className="text-white" />
            </motion.div>
            
            <h2 className="text-2xl font-black mb-2" style={{ color: COLORS.text }}>
              Bestellung bestätigt!
            </h2>
            <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
              Deine Bestellung wird jetzt zubereitet
            </p>

            <div className="p-5 rounded-2xl text-left mb-6" style={{ backgroundColor: COLORS.white }}>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm" style={{ color: COLORS.textMuted }}>Bestellnummer</span>
                <span className="font-black text-xl" style={{ color: COLORS.green }}>
                  #{orderResult.order_number}
                </span>
              </div>
              {orderResult.estimated_minutes && (
                <div className="flex items-center gap-3 text-sm" style={{ color: COLORS.textMuted }}>
                  <Clock size={18} />
                  <span>Fertig in ca. {orderResult.estimated_minutes} Min.</span>
                </div>
              )}
              {orderType === "pickup" && (
                <div className="flex items-center gap-3 text-sm mt-2" style={{ color: COLORS.textMuted }}>
                  <Storefront size={18} />
                  <span>Abholung: Pontstraße 137, Aachen</span>
                </div>
              )}
              {orderType === "delivery" && (
                <div className="flex items-center gap-3 text-sm mt-2" style={{ color: COLORS.textMuted }}>
                  <MapPin size={18} />
                  <span>{address}, {plz} {city}</span>
                </div>
              )}
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={onClose}
              className="w-full py-4 rounded-2xl font-black text-white"
              style={{ backgroundColor: COLORS.green }}
            >
              Fertig
            </motion.button>
          </div>
        )}

        {/* ═══ STEP 1: Address & Delivery ═══ */}
        {step === 1 && (
          <>
            {/* Header */}
            <div className="p-5 flex items-center justify-between" style={{ backgroundColor: COLORS.white }}>
              <div className="flex items-center gap-3">
                <motion.button 
                  whileTap={{ scale: 0.9 }} 
                  onClick={() => setStep(0)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: COLORS.bgDark }}
                >
                  <CaretLeft size={20} weight="bold" style={{ color: COLORS.text }} />
                </motion.button>
                <div>
                  <h2 className="font-black text-lg" style={{ color: COLORS.text }}>
                    {orderType === "delivery" ? "Lieferadresse" : "Abholung"}
                  </h2>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Schritt 1 von 2</p>
                </div>
              </div>
              <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: COLORS.bgDark }}>
                <X size={20} weight="bold" style={{ color: COLORS.text }} />
              </motion.button>
            </div>

            <div className="overflow-y-auto p-5 space-y-5" style={{ maxHeight: "calc(90vh - 200px)" }}>
              
              {/* Delivery Zone Info (wenn Lieferung) */}
              {orderType === "delivery" && deliveryInfo && (
                <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.green + "10", border: `2px solid ${COLORS.green}` }}>
                  <div className="flex items-center gap-3">
                    <Check size={20} weight="bold" style={{ color: COLORS.green }} />
                    <div>
                      <p className="font-bold text-sm" style={{ color: COLORS.green }}>
                        Lieferung nach {deliveryInfo.name || plz} möglich!
                      </p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>
                        Liefergebühr: {deliveryFee.toFixed(2)}€ · Mindestbestellung: {minOrder.toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Gespeicherte Adresse übernehmen - für eingeloggte User */}
              {orderType === "delivery" && user && user.address && (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    setAddress(user.address || "");
                    setCity(user.city || "Aachen");
                    setPhone(user.phone || "");
                    toast.success("Adresse übernommen!");
                  }}
                  className="w-full p-4 rounded-2xl flex items-center gap-4"
                  style={{ backgroundColor: COLORS.orange + "15", border: `2px dashed ${COLORS.orange}` }}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: COLORS.orange }}>
                    <User size={20} weight="fill" className="text-white" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-sm" style={{ color: COLORS.text }}>
                      Gespeicherte Adresse übernehmen
                    </p>
                    <p className="text-xs" style={{ color: COLORS.textMuted }}>
                      {user.address}{user.city ? `, ${user.city}` : ""}
                    </p>
                  </div>
                  <CaretRight size={20} style={{ color: COLORS.orange }} />
                </motion.button>
              )}

              {/* Pickup Info */}
              {orderType === "pickup" && (
                <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.orange + "10", border: `2px solid ${COLORS.orange}` }}>
                  <div className="flex items-center gap-3">
                    <Storefront size={24} weight="fill" style={{ color: COLORS.orange }} />
                    <div>
                      <p className="font-bold text-sm" style={{ color: COLORS.orange }}>Abholung</p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>
                        Pontstraße 137, 52062 Aachen · Keine Liefergebühr
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Delivery Address - nur bei Lieferung */}
              {orderType === "delivery" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: COLORS.textMuted }}>
                      Straße & Hausnummer *
                    </label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="z.B. Pontstraße 137"
                      className="w-full p-4 rounded-2xl font-medium"
                      style={{ backgroundColor: COLORS.white, color: COLORS.text, border: `2px solid ${COLORS.border}` }}
                      data-testid="address-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <div className="w-1/3">
                      <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: COLORS.textMuted }}>
                        PLZ
                      </label>
                      <input
                        type="text"
                        value={plz}
                        disabled
                        className="w-full p-4 rounded-2xl font-medium"
                        style={{ backgroundColor: COLORS.bgDark, color: COLORS.text, border: `2px solid ${COLORS.border}` }}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: COLORS.textMuted }}>
                        Stadt
                      </label>
                      <input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="Aachen"
                        className="w-full p-4 rounded-2xl font-medium"
                        style={{ backgroundColor: COLORS.white, color: COLORS.text, border: `2px solid ${COLORS.border}` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Phone - PFLICHT */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: COLORS.textMuted }}>
                  Telefon (für Rückfragen) *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 151 12345678"
                  className="w-full p-4 rounded-2xl font-medium"
                  style={{ backgroundColor: COLORS.white, color: COLORS.text, border: `2px solid ${COLORS.border}` }}
                  data-testid="phone-input"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider mb-2 block" style={{ color: COLORS.textMuted }}>
                  Anmerkungen (optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="z.B. 2. Stock, Klingel oben rechts"
                  rows={2}
                  className="w-full p-4 rounded-2xl font-medium resize-none"
                  style={{ backgroundColor: COLORS.white, color: COLORS.text, border: `2px solid ${COLORS.border}` }}
                />
              </div>

              {/* Membership Upsell Banner */}
              {showMembershipUpsell && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl"
                  style={{ backgroundColor: "#9B59B6" + "15", border: `2px solid #9B59B6` }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: "#9B59B6" }}>
                      <span className="text-lg">👑</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm" style={{ color: COLORS.text }}>
                        Spare bis zu {potentialMonthlySavings.toFixed(0)}€/Monat!
                      </p>
                      <p className="text-xs mt-1" style={{ color: COLORS.textMuted }}>
                        Mit der Flatrate: Unbegrenzte Getränke für nur 39,90€/Monat
                      </p>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => { onClose(); navigate("/membership"); }}
                        className="mt-2 px-4 py-2 rounded-xl text-sm font-bold text-white"
                        style={{ backgroundColor: "#9B59B6" }}
                      >
                        Jetzt Flatrate sichern
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t" style={{ backgroundColor: COLORS.white, borderColor: COLORS.border }}>
              {/* Zusammenfassung */}
              <div className="flex justify-between mb-2 text-sm">
                <span style={{ color: COLORS.textMuted }}>Zwischensumme</span>
                <span style={{ color: COLORS.text }}>{subtotal.toFixed(2)}€</span>
              </div>
              {orderType === "delivery" && (
                <div className="flex justify-between mb-2 text-sm">
                  <span style={{ color: COLORS.textMuted }}>Lieferung</span>
                  <span style={{ color: COLORS.text }}>{deliveryFee.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between mb-4 font-black text-lg">
                <span>Gesamt</span>
                <span style={{ color: COLORS.green }}>{total.toFixed(2)}€</span>
              </div>
              
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={handleContinueToPayment}
                className="w-full py-4 rounded-2xl font-black text-white flex items-center justify-center gap-2"
                style={{ backgroundColor: COLORS.green, boxShadow: `0 6px 20px ${COLORS.green}40` }}
                data-testid="continue-to-payment-btn"
              >
                Weiter zur Zahlung
                <ArrowRight size={20} weight="bold" />
              </motion.button>
            </div>
          </>
        )}

        {/* ═══ STEP 2: Payment Selection ═══ */}
        {step === 2 && (
          <>
            {/* Header */}
            <div className="p-5 flex items-center justify-between" style={{ backgroundColor: COLORS.white }}>
              <div className="flex items-center gap-3">
                <motion.button 
                  whileTap={{ scale: 0.9 }} 
                  onClick={() => setStep(1)}
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: COLORS.bgDark }}
                >
                  <CaretLeft size={20} weight="bold" style={{ color: COLORS.text }} />
                </motion.button>
                <div>
                  <h2 className="font-black text-lg" style={{ color: COLORS.text }}>Zahlung</h2>
                  <p className="text-xs" style={{ color: COLORS.textMuted }}>Schritt 2 von 2</p>
                </div>
              </div>
              <motion.button whileTap={{ scale: 0.9 }} onClick={onClose}
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: COLORS.bgDark }}>
                <X size={20} weight="bold" style={{ color: COLORS.text }} />
              </motion.button>
            </div>

            <div className="overflow-y-auto p-5 space-y-5" style={{ maxHeight: "calc(90vh - 200px)" }}>
              {/* Payment Methods */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: COLORS.textMuted }}>
                  Zahlungsmethode wählen
                </p>
                <div className="space-y-2">
                  {/* Cash */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setPaymentMethod("cash")}
                    className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all"
                    style={{
                      backgroundColor: paymentMethod === "cash" ? COLORS.green + "15" : COLORS.white,
                      border: `2px solid ${paymentMethod === "cash" ? COLORS.green : COLORS.border}`
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: paymentMethod === "cash" ? COLORS.green : COLORS.bgDark }}>
                      <span className="text-2xl">{paymentMethod === "cash" ? "💵" : "💵"}</span>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold" style={{ color: COLORS.text }}>Barzahlung</p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>Bei Lieferung/Abholung zahlen</p>
                    </div>
                    {paymentMethod === "cash" && (
                      <Check size={24} weight="bold" style={{ color: COLORS.green }} />
                    )}
                  </motion.button>

                  {/* Card at Door */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setPaymentMethod("card")}
                    className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all"
                    style={{
                      backgroundColor: paymentMethod === "card" ? COLORS.green + "15" : COLORS.white,
                      border: `2px solid ${paymentMethod === "card" ? COLORS.green : COLORS.border}`
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: paymentMethod === "card" ? COLORS.green : COLORS.bgDark }}>
                      <CreditCard size={24} weight="fill" style={{ color: paymentMethod === "card" ? COLORS.white : COLORS.textMuted }} />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold" style={{ color: COLORS.text }}>Kartenzahlung</p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>EC/Kreditkarte bei Lieferung</p>
                    </div>
                    {paymentMethod === "card" && (
                      <Check size={24} weight="bold" style={{ color: COLORS.green }} />
                    )}
                  </motion.button>

                  {/* Online Payment (Stripe) - Coming Soon */}
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setPaymentMethod("online")}
                    className="w-full p-4 rounded-2xl flex items-center gap-4 transition-all"
                    style={{
                      backgroundColor: paymentMethod === "online" ? COLORS.green + "15" : COLORS.white,
                      border: `2px solid ${paymentMethod === "online" ? COLORS.green : COLORS.border}`
                    }}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: paymentMethod === "online" ? COLORS.green : COLORS.bgDark }}>
                      <span className="text-xl">💳</span>
                    </div>
                    <div className="flex-1 text-left">
                      <p className="font-bold" style={{ color: COLORS.text }}>Online bezahlen</p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>Apple Pay, Google Pay, Kreditkarte</p>
                    </div>
                    {paymentMethod === "online" && (
                      <Check size={24} weight="bold" style={{ color: COLORS.green }} />
                    )}
                  </motion.button>
                </div>
              </div>

              {/* Order Summary */}
              <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.white }}>
                <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: COLORS.textMuted }}>
                  Zusammenfassung
                </p>
                {cart.slice(0, 3).map(item => (
                  <div key={item.id} className="flex justify-between text-sm py-1.5">
                    <span style={{ color: COLORS.text }}>{item.quantity}x {item.product_name}</span>
                    <span style={{ color: COLORS.textMuted }}>{item.total_price.toFixed(2)}€</span>
                  </div>
                ))}
                {cart.length > 3 && (
                  <p className="text-xs py-1" style={{ color: COLORS.textMuted }}>
                    + {cart.length - 3} weitere Artikel
                  </p>
                )}
                <div className="border-t mt-3 pt-3 space-y-1.5" style={{ borderColor: COLORS.border }}>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: COLORS.textMuted }}>Zwischensumme</span>
                    <span>{subtotal.toFixed(2)}€</span>
                  </div>
                  {orderType === "delivery" && (
                    <div className="flex justify-between text-sm">
                      <span style={{ color: COLORS.textMuted }}>Lieferung</span>
                      <span>{deliveryFee.toFixed(2)}€</span>
                    </div>
                  )}
                  <div className="flex justify-between font-black text-lg pt-2">
                    <span>Gesamt</span>
                    <span style={{ color: COLORS.green }}>{total.toFixed(2)}€</span>
                  </div>
                </div>
              </div>

              {/* Delivery Info */}
              <div className="p-4 rounded-2xl" style={{ backgroundColor: COLORS.bgDark }}>
                <div className="flex items-center gap-3">
                  {orderType === "delivery" ? (
                    <>
                      <MapPin size={20} style={{ color: COLORS.textMuted }} />
                      <div className="flex-1">
                        <p className="font-bold text-sm" style={{ color: COLORS.text }}>{address}</p>
                        <p className="text-xs" style={{ color: COLORS.textMuted }}>{plz} {city}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Storefront size={20} style={{ color: COLORS.textMuted }} />
                      <div className="flex-1">
                        <p className="font-bold text-sm" style={{ color: COLORS.text }}>Abholung</p>
                        <p className="text-xs" style={{ color: COLORS.textMuted }}>Pontstraße 137, 52062 Aachen</p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t" style={{ backgroundColor: COLORS.white, borderColor: COLORS.border }}>
              {/* Login Hinweis wenn nicht angemeldet */}
              {!user && (
                <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: COLORS.orange + "15", border: `2px solid ${COLORS.orange}` }}>
                  <div className="flex items-center gap-3">
                    <User size={24} weight="fill" style={{ color: COLORS.orange }} />
                    <div className="flex-1">
                      <p className="font-bold text-sm" style={{ color: COLORS.text }}>
                        Noch nicht angemeldet
                      </p>
                      <p className="text-xs" style={{ color: COLORS.textMuted }}>
                        Melde dich an, um deine Bestellung abzuschließen
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <motion.button
                whileTap={{ scale: loading ? 1 : 0.97 }}
                onClick={() => {
                  if (!user) {
                    // Zeige Login Modal
                    onClose();
                    setTimeout(() => {
                      document.dispatchEvent(new CustomEvent('show-auth-modal'));
                    }, 300);
                    toast("Bitte melde dich zuerst an", { icon: "👤" });
                  } else {
                    handleSubmitOrder();
                  }
                }}
                disabled={loading}
                className="w-full py-4 rounded-2xl font-black text-white flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: user ? COLORS.green : COLORS.orange,
                  boxShadow: `0 6px 20px ${user ? COLORS.green : COLORS.orange}40`,
                  opacity: loading ? 0.7 : 1
                }}
                data-testid="submit-order-btn"
              >
                {loading ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <Coffee size={22} />
                  </motion.div>
                ) : user ? (
                  <>
                    Jetzt bestellen · {total.toFixed(2)}€
                  </>
                ) : (
                  <>
                    <User size={20} weight="bold" />
                    Anmelden & Bestellen
                  </>
                )}
              </motion.button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// PRODUCT CARD - Modern Grid Card
// ═══════════════════════════════════════════════════════════════════════════
const ProductCard = ({ product, index, onSelect }) => {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(product)}
      className="text-left rounded-2xl overflow-hidden group"
      style={{ backgroundColor: COLORS.card }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={product.image} 
          alt={product.name} 
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        
        {/* Popular Badge */}
        {product.popular && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-lg backdrop-blur-sm"
            style={{ backgroundColor: "rgba(45,90,61,0.9)" }}>
            <Sparkle size={10} weight="fill" className="text-white" />
            <span className="text-[10px] font-bold text-white tracking-wide">BELIEBT</span>
          </div>
        )}

        {/* Rating */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg backdrop-blur-sm"
          style={{ backgroundColor: "rgba(255,255,255,0.95)" }}>
          <Star size={12} weight="fill" style={{ color: "#F59E0B" }} />
          <span className="text-xs font-bold">{product.rating}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-3">
        <h3 className="font-bold text-sm truncate" style={{ color: COLORS.text }}>{product.name}</h3>
        <p className="text-xs mt-0.5 truncate" style={{ color: COLORS.textMuted }}>{product.description}</p>
        
        <div className="flex items-center justify-between mt-3">
          <span className="text-lg font-black" style={{ color: COLORS.green }}>{product.price.toFixed(2)}€</span>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
            style={{ backgroundColor: COLORS.green }}>
            <Plus size={16} weight="bold" className="text-white" />
          </div>
        </div>
      </div>
    </motion.button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// STICKY CART BAR - Bottom Fixed
// ═══════════════════════════════════════════════════════════════════════════
const StickyCartBar = ({ cartCount, subtotal, onClick }) => {
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-40 p-4"
      style={{ 
        background: `linear-gradient(to top, ${COLORS.bg} 70%, transparent)`,
        paddingBottom: "max(1rem, env(safe-area-inset-bottom))"
      }}
    >
      <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className="w-full p-4 rounded-2xl flex items-center justify-between"
        style={{ 
          backgroundColor: COLORS.green,
          boxShadow: `0 -4px 30px ${COLORS.green}50`
        }}
        data-testid="sticky-cart-btn"
      >
        <div className="flex items-center gap-3 text-white">
          <div className="relative">
            <ShoppingBag size={24} weight="fill" />
            <span 
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs font-black flex items-center justify-center"
              style={{ backgroundColor: COLORS.orange }}
            >
              {cartCount}
            </span>
          </div>
          <span className="font-bold">Warenkorb ansehen</span>
        </div>
        <span className="font-black text-xl text-white">{subtotal.toFixed(2)}€</span>
      </motion.button>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// AUTH PROMPT MODAL
// ═══════════════════════════════════════════════════════════════════════════
const AuthPromptModal = ({ isOpen, onClose, onNavigate }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(26,26,26,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="w-full max-w-sm p-8 rounded-3xl text-center"
        style={{ backgroundColor: COLORS.white }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
          style={{ backgroundColor: COLORS.orange + "20" }}>
          <User size={32} weight="fill" style={{ color: COLORS.orange }} />
        </div>
        <h3 className="text-xl font-black mb-2" style={{ color: COLORS.text }}>Anmeldung erforderlich</h3>
        <p className="text-sm mb-6" style={{ color: COLORS.textMuted }}>
          Bitte melde dich an, um deine Bestellung aufzugeben.
        </p>
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={onNavigate}
          className="w-full py-4 rounded-2xl font-black text-white"
          style={{ backgroundColor: COLORS.green }}
        >
          Zur Anmeldung
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE - COMING SOON
// ═══════════════════════════════════════════════════════════════════════════
export default function OnlineOrderPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: COLORS.bg }} data-testid="online-order-page">
      {/* Header */}
      <header className="px-4 py-4" style={{ backgroundColor: COLORS.bg }}>
        <div className="flex items-center gap-3 max-w-2xl mx-auto">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => navigate("/landing")}
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.white }}
            data-testid="back-btn"
          >
            <CaretLeft size={22} weight="bold" style={{ color: COLORS.text }} />
          </motion.button>
          <div>
            <h1 className="text-xl font-black tracking-tight" style={{ color: COLORS.text }}>
              Online Bestellen
            </h1>
            <p className="text-xs" style={{ color: COLORS.textMuted }}>Bald verfügbar</p>
          </div>
        </div>
      </header>

      {/* Coming Soon Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          {/* Animated Icon */}
          <motion.div
            animate={{ 
              y: [0, -15, 0],
              rotate: [0, 5, -5, 0]
            }}
            transition={{ 
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="mb-8"
          >
            <div 
              className="w-32 h-32 mx-auto rounded-3xl flex items-center justify-center"
              style={{ backgroundColor: COLORS.orange + "20" }}
            >
              <Coffee size={64} weight="fill" style={{ color: COLORS.orange }} />
            </div>
          </motion.div>

          {/* Title */}
          <h2 className="text-3xl font-black mb-3" style={{ color: COLORS.text }}>
            Bald verfügbar!
          </h2>
          
          {/* Subtitle */}
          <p className="text-lg mb-6" style={{ color: COLORS.textMuted }}>
            Wir arbeiten gerade daran, dass du bald online bestellen und dir dein Lieblingsgetränk abholen oder liefern lassen kannst.
          </p>

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2 text-sm">
              <span style={{ color: COLORS.textMuted }}>Entwicklung</span>
              <span className="font-bold" style={{ color: COLORS.green }}>75%</span>
            </div>
            <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: COLORS.bgDark }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "75%" }}
                transition={{ duration: 1.5, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ backgroundColor: COLORS.green }}
              />
            </div>
          </div>

          {/* Features Coming */}
          <div className="space-y-3 mb-8 text-left">
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: COLORS.white }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.green + "15" }}>
                <ShoppingBag size={20} weight="fill" style={{ color: COLORS.green }} />
              </div>
              <div>
                <p className="font-bold text-sm" style={{ color: COLORS.text }}>Online Speisekarte</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Alle Produkte durchstöbern</p>
              </div>
              <Check size={18} weight="bold" style={{ color: COLORS.green }} />
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: COLORS.white }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.orange + "15" }}>
                <Storefront size={20} weight="fill" style={{ color: COLORS.orange }} />
              </div>
              <div>
                <p className="font-bold text-sm" style={{ color: COLORS.text }}>Click & Collect</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Vorbestellen & abholen</p>
              </div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <Clock size={18} weight="bold" style={{ color: COLORS.orange }} />
              </motion.div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-xl opacity-60" style={{ backgroundColor: COLORS.white }}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: COLORS.textMuted + "15" }}>
                <MapPin size={20} weight="fill" style={{ color: COLORS.textMuted }} />
              </div>
              <div>
                <p className="font-bold text-sm" style={{ color: COLORS.text }}>Lieferservice</p>
                <p className="text-xs" style={{ color: COLORS.textMuted }}>Direkt zu dir nach Hause</p>
              </div>
              <span className="text-xs font-bold px-2 py-1 rounded-full" style={{ backgroundColor: COLORS.textMuted + "20", color: COLORS.textMuted }}>
                Soon
              </span>
            </div>
          </div>

          {/* CTA */}
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/home")}
            className="w-full py-4 rounded-2xl font-black text-white mb-4"
            style={{ backgroundColor: COLORS.green }}
          >
            Zu den Coupons
          </motion.button>

          <p className="text-xs" style={{ color: COLORS.textMuted }}>
            Bis dahin: Nutze unsere Coupons & Stempelkarte!
          </p>
        </motion.div>
      </main>
    </div>
  );
}
